/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AddToCartOptions,
  Article,
  Cart,
  DeliveryType,
  LocalUser,
  Location,
  OrderAddressInput,
  OrderDelivery,
  OrderDeliveryAddress,
  OrderDeliveryInput,
  OrderItem,
  QuantityOptions,
} from './types';

export function extractAddress({
  user,
  isCustomer,
}: {
  user: any;
  isCustomer: boolean;
}) {
  if (isCustomer) {
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      addressLine1: user.addressLine1,
      addressLine2: user.addressLine2,
      addressLocality: user.addressLocality,
      addressPostcode: user.addressPostcode,
      country: 'DE',
      company: '',
    };
  } else {
    return {
      firstName: user.givenName,
      lastName: user.familyName,
      addressLine1: user.primaryAddress.addressLine1,
      addressLine2: user.primaryAddress.addressLine2,
      addressLocality: user.primaryAddress.addressLocality,
      addressPostcode: user.primaryAddress.addressPostcode,
      country: 'DE',
      company: '',
    };
  }
}

// const nanoid = customAlphabet('0123456789', 4);
function nanoid(length = 4) {
  let id = '';
  for (let i = 0; i < length; i++) {
    id += Math.floor(Math.random() * 10);
  }
  return id;
}

// start of atoms for adding item to cart
export function findExistingIndices<A extends Article>(
  selectedCart: OrderDelivery[],
  newCartItem: A,
  deliveryType: DeliveryType | string,
) {
  let existingDeliveryIndex = -1;
  let existingItemIndex = -1;

  for (
    let deliveryIndex = 0;
    deliveryIndex < selectedCart?.length;
    deliveryIndex++
  ) {
    const delivery = selectedCart[deliveryIndex];
    if (delivery.type === deliveryType) {
      existingDeliveryIndex = deliveryIndex;
      for (let itemIndex = 0; itemIndex < delivery.items.length; itemIndex++) {
        const item = delivery.items[itemIndex];
        if (item.catalogueID === newCartItem.id) {
          existingItemIndex = itemIndex;
          break;
        }
      }
    }
    if (existingItemIndex !== -1) break;
  }
  return { existingDeliveryIndex, existingItemIndex };
}

export function calculateLineValues<A extends Article>(
  newCartItem: A,
  options: AddToCartOptions,
) {
  const i = newCartItem;
  const quantity = options.quantity ? options.quantity : 1;
  const inventoryItem = i.inventory ? i.inventory[0] : null;

  const price =
    options.price !== null
      ? options.price
      : inventoryItem?.salePrice
      ? inventoryItem?.salePrice
      : inventoryItem?.basePrice;

  const lineTotalNet = quantity && price ? quantity * price : 0;
  const taxRate = inventoryItem?.taxRate ? inventoryItem.taxRate / 100 : 0;
  const lineTax = lineTotalNet ? lineTotalNet * taxRate : 0;

  return { lineTotalNet, lineTax, price, quantity, article: i };
}

export function updateExistingItem(
  selectedCart: OrderDelivery[],
  existingDeliveryIndex: number,
  existingItemIndex: number,
  quantity: number,
  lineTotalNet: number,
  lineTax: number,
  price?: number,
) {
  selectedCart[existingDeliveryIndex].items[existingItemIndex].quantity =
    quantity;
  selectedCart[existingDeliveryIndex].items[existingItemIndex].lineTotalGross =
    lineTotalNet + lineTax;
  selectedCart[existingDeliveryIndex].items[existingItemIndex].lineTax =
    lineTax;
  selectedCart[existingDeliveryIndex].items[existingItemIndex].lineTotalNet =
    lineTotalNet;
  // to handle adding same produc to the cart.
  if (price) {
    selectedCart[existingDeliveryIndex].items[existingItemIndex].price = price;
  }
  return selectedCart;
}

export function createNewDeliveryItem<A extends Article>(
  quantity: number,
  price: number,
  lineTotalNet: number,
  lineTax: number,
  i: A,
) {
  const inventoryItem = i.inventory ? i.inventory[0] : null;

  const deliveryItem: OrderItem = {
    amount: i.amount,
    catalogueID: i.id,
    inventoryID: i.inventory[0].id,
    lineTotalGross: lineTotalNet + lineTax,
    lineTax,
    lineTotalNet,
    name: i.name,
    price: price as number,
    quantity,
    taxRate: inventoryItem?.taxRate || 0,
    unit: i.unit,
    quantityOptions: i.quantityOptions as QuantityOptions,
    loose: i.loose,
    locations: inventoryItem?.locations as Location[],
  };
  return deliveryItem;
}

export function createNewOrderDelivery(
  user: LocalUser | null,
  deliveryItem: OrderItem,
  deliveryType: DeliveryType | string,
  customerAddress?: OrderAddressInput,
) {
  const address =
    customerAddress || extractAddress({ user, isCustomer: false });

  const orderDelivery: OrderDelivery = {
    deliveryAddress: {
      ...address,
      id: user?.primaryAddress?.id || '1',
    },
    items: [deliveryItem],
    type: deliveryType as DeliveryType,
    id: nanoid(),
    comments: '',
    deliveryFrom: '',
    deliveryTo: '',
    locationID: deliveryItem.locations ? deliveryItem.locations[0].id : '1',
  };
  return orderDelivery;
}

// end of atoms for adding item to cart

export function removeItemFromCart(
  cart: Cart,
  key: string,
  itemId: string,
  quantity: number,
  itemType: string,
) {
  const selectedCart = cart[key];
  if (!selectedCart) return { selectedCart, itemRemoved: false }; // No such cart.

  let itemRemoved = false;
  for (
    let deliveryIndex = 0;
    deliveryIndex < selectedCart.length;
    deliveryIndex++
  ) {
    const delivery = selectedCart[deliveryIndex];

    // Loop over each item in the delivery
    for (let itemIndex = 0; itemIndex < delivery.items.length; itemIndex++) {
      const item = delivery.items[itemIndex];
      if (item.catalogueID == itemId && item.quantity) {
        if (item.quantity > 1) {
          if (quantity) {
            if (itemType === 'loose') {
              item.quantity -= quantity;
            } else {
              item.quantity = quantity;
            }
          } else {
            item.quantity--;
          }
        } else {
          // Remove the item from the delivery
          delivery.items.splice(itemIndex, 1);
        }

        itemRemoved = true;

        // If the delivery has no more items, remove it
        if (delivery.items.length === 0) {
          selectedCart.splice(deliveryIndex, 1);
        }

        break;
      }
    }

    // If we've removed an item, we can break out of the loop
    if (itemRemoved) break;
  }
  return { selectedCart, itemRemoved };
}

export const removeItemFromDelivery = (
  cart: Cart,
  key: string,
  deliveryId: string,
  catalogueIds: string[],
) => {
  if (!cart[key]) return { cart, deliveryRemoved: false }; // No such cart.

  const deliveryIndex = cart[key].findIndex(
    (delivery) => delivery.id === deliveryId,
  );
  if (deliveryIndex === -1) return { cart, deliveryRemoved: false }; // No such delivery.

  // Remove items with matching catalogueIds.
  cart[key][deliveryIndex].items = cart[key][deliveryIndex].items.filter(
    (item) => !catalogueIds.includes(item.catalogueID.toString()),
  );

  // Remove the delivery if it is empty.
  if (cart[key][deliveryIndex].items.length === 0) {
    cart[key].splice(deliveryIndex, 1);
  }

  return { cart, deliveryRemoved: true };
};

export const moveBetweenDeliveries = (
  cart: Cart,
  key: string,
  sourceDeliveryId: string,
  targetDeliveryId: null | string,
  catalogueIds: string[],
  siloId?: string,
) => {
  const selectedCart: OrderDelivery[] = cart[key];

  if (!selectedCart) return { selectedCart, moved: false };

  const sourceDeliveryIndex = selectedCart.findIndex(
    (delivery) => delivery.id === sourceDeliveryId,
  );

  if (sourceDeliveryIndex === -1) return { selectedCart, moved: false };

  // Collect the items to be moved and remove them from the source delivery.
  const itemsToMove: OrderItem[] = [];
  selectedCart[sourceDeliveryIndex].items = selectedCart[
    sourceDeliveryIndex
  ].items.filter((item) => {
    if (catalogueIds.includes(item.catalogueID.toString())) {
      itemsToMove.push(item);
      return false;
    } else {
      return true;
    }
  });

  // TODO: please refactor this
  const idsToHandle = [null, '0', 'SILO'];
  if (idsToHandle.includes(targetDeliveryId)) {
    // Create a new delivery with the items to move and a new ID
    const newDelivery: OrderDelivery = {
      id: nanoid(),
      items: itemsToMove,
      deliveryAddress: selectedCart[sourceDeliveryIndex].deliveryAddress,
      type:
        targetDeliveryId === '0' ? DeliveryType.Pickup : DeliveryType.Delivery,
      comments: '',
      deliveryFrom: '',
      deliveryTo: '',
      locationID: itemsToMove[0].locations
        ? itemsToMove[0].locations[0].id
        : '1',
      siloID: targetDeliveryId === 'SILO' ? siloId : null,
    };
    selectedCart.push(newDelivery);
  } else {
    // Find the target delivery and move the items
    const targetDeliveryIndex = selectedCart.findIndex(
      (delivery) => delivery.id === targetDeliveryId,
    );

    if (targetDeliveryIndex === -1) {
      return { selectedCart, moved: false };
    } else {
      // Add the items to the target delivery.
      itemsToMove.forEach((itemToMove) => {
        selectedCart[targetDeliveryIndex].items.push(itemToMove);
      });
    }
  }

  // If the source delivery is now empty, remove it.
  if (selectedCart[sourceDeliveryIndex].items.length === 0) {
    selectedCart.splice(sourceDeliveryIndex, 1);
  }

  return { selectedCart, moved: true };
};

export const setCartItemQuantity = (
  cart: Cart,
  key: string,
  quantity: number,
  deliveryId: string,
  catalogueId: string,
) => {
  const selectedCart = cart[key];

  if (!selectedCart || quantity < 0)
    return { selectedCart, quantityUpdated: false };

  const deliveryToUpdate = selectedCart.find(
    (delivery) => delivery.id === deliveryId,
  );

  if (!deliveryToUpdate) return { selectedCart, quantityUpdated: false };

  const itemIndex = deliveryToUpdate.items.findIndex(
    (item) => item.catalogueID === catalogueId,
  );

  // If quantity is 0, remove the item from the cart.
  if (quantity === 0) {
    deliveryToUpdate.items.splice(itemIndex, 1);
    return { cart, quantityUpdated: true };
  }

  const itemToUpdate = deliveryToUpdate.items[itemIndex];

  if (!itemToUpdate) return { selectedCart, quantityUpdated: false };

  const lineTotalNet =
    quantity && itemToUpdate.price ? quantity * itemToUpdate.price : 0;
  const taxRate = itemToUpdate.taxRate ? itemToUpdate.taxRate / 100 : 0;
  const lineTax = lineTotalNet ? lineTotalNet * taxRate : 0;

  itemToUpdate.quantity = quantity;
  itemToUpdate.lineTotalNet = lineTotalNet;
  itemToUpdate.lineTax = lineTax;
  itemToUpdate.lineTotalGross = lineTotalNet + lineTax;

  return { cart, quantityUpdated: true };
};

export const changeSiloId = (
  cart: Cart,
  key: string,
  deliveryId: string,
  newSiloId: string,
) => {
  const selectedCart = cart[key];
  if (!selectedCart) return { cart, idUpdated: false };

  const deliveryToUpdate = selectedCart.find(
    (delivery) => delivery.id === deliveryId,
  );
  if (!deliveryToUpdate) return { cart, idUpdated: false };

  // Update the delivery address
  deliveryToUpdate.siloID = newSiloId;

  return { cart, idUpdated: true };
};

export const changeLocationID = (
  cart: Cart,
  key: string,
  deliveryId: string,
  newLocationId: string,
) => {
  const selectedCart = cart[key];
  if (!selectedCart) return { cart, idUpdated: false };

  const deliveryToUpdate = selectedCart.find(
    (delivery) => delivery.id === deliveryId,
  );
  if (!deliveryToUpdate) return { cart, idUpdated: false };

  // Update the delivery address
  deliveryToUpdate.locationID = newLocationId;

  return { cart, idUpdated: true };
};

export const changeDeliveryDates = (
  cart: Cart,
  key: string,
  deliveryId: string,
  deliveryFrom: string | undefined,
  deliveryTo: string | undefined,
) => {
  const selectedCart = cart[key];
  if (!selectedCart) return { cart, dateUpdated: false };
  const deliveryToUpdate = selectedCart.find(
    (delivery) => delivery.id === deliveryId,
  );
  if (!deliveryToUpdate) return { cart, dateUpdated: false };

  // Update the delivery address
  if (deliveryFrom) deliveryToUpdate.deliveryFrom = deliveryFrom;
  if (deliveryTo) deliveryToUpdate.deliveryTo = deliveryTo;

  return { cart, dateUpdated: true };
};

export const changeDeliveryAddress = (
  cart: Cart,
  key: string,
  deliveryId: string,
  newAddress: OrderDeliveryAddress,
) => {
  const selectedCart = cart[key];
  if (!selectedCart) return { cart, addressUpdated: false };

  const deliveryToUpdate = selectedCart.find(
    (delivery) => delivery.id === deliveryId,
  );
  if (!deliveryToUpdate) return { cart, addressUpdated: false };

  // Update the delivery address
  deliveryToUpdate.deliveryAddress = newAddress;

  return { cart, addressUpdated: true };
};

export const removeIdsFromRetailerCart = (
  cart: OrderDelivery[],
): OrderDeliveryInput[] => {
  // Deep clone the cart
  const clonedCart: OrderDelivery[] = JSON.parse(JSON.stringify(cart));

  // Loop through the cloned cart and remove the ids
  const processedCart: OrderDeliveryInput[] = clonedCart.map((delivery) => {
    // Remove the id from delivery
    const { id, ...deliveryWithoutId } = delivery;

    // If deliveryAddress exists
    if (
      deliveryWithoutId.deliveryAddress &&
      'id' in deliveryWithoutId.deliveryAddress
    ) {
      // Remove the id from deliveryAddress
      const { id, ...addressWithoutId } = deliveryWithoutId.deliveryAddress;
      deliveryWithoutId.deliveryAddress = addressWithoutId as any; // type cast here
    }

    // If items exist
    if (deliveryWithoutId.items) {
      deliveryWithoutId.items = deliveryWithoutId.items.map((item) => {
        // Remove the properties from item
        const { lineTax, loose, quantityOptions, locations, ...itemWithoutId } =
          item;
        console.log({ itemWithoutId });
        return itemWithoutId;
      }) as any;
    }

    return deliveryWithoutId as OrderDeliveryInput; // type cast here
  });

  return processedCart;
};

export const calculateCartTotalItems = (
  selectedCart: OrderDelivery[],
): number => {
  return selectedCart.reduce((acc, delivery) => acc + delivery.items.length, 0);
};
